import { Link } from 'react-router-dom';
import { Cookie, Mail, CheckCircle, XCircle } from 'lucide-react';
import PageLayout from '../components/PageLayout';

function Section({ id, title, children }) {
  return (
    <section id={id} className="mb-12 scroll-mt-20">
      <h2 className="text-xl font-bold text-white mb-4 pb-3 border-b border-white/[0.06]">
        {title}
      </h2>
      <div className="space-y-4 text-slate-400 text-sm leading-relaxed">
        {children}
      </div>
    </section>
  );
}


const TOC = [
  { id: 'what-are-cookies', label: '1. What Are Cookies?' },
  { id: 'how-we-use',       label: '2. How We Use Cookies' },
  { id: 'cookie-table',     label: '3. Cookie Reference' },
  { id: 'no-tracking',      label: '4. What We Don\'t Do' },
  { id: 'third-party',      label: '5. Third-Party Cookies' },
  { id: 'managing',         label: '6. Managing Cookies' },
  { id: 'consent',          label: '7. Your Consent' },
  { id: 'changes',          label: '8. Changes' },
  { id: 'contact',          label: '9. Contact' },
];

const COOKIES = [
  {
    name: 'zc_session',
    category: 'Essential',
    purpose: 'Maintains your authenticated session after login. Without this cookie, you would be logged out on every page navigation.',
    duration: 'Session (deleted on browser close)',
    firstParty: true,
  },
  {
    name: 'zc_token',
    category: 'Essential',
    purpose: 'Stores your JWT authentication token for API requests. Required for all authenticated operations.',
    duration: '7 days',
    firstParty: true,
  },
  {
    name: 'zc_prefs',
    category: 'Functional',
    purpose: 'Stores your UI preferences such as notification settings and dashboard display options.',
    duration: '1 year',
    firstParty: true,
  },
  {
    name: 'zc_csrf',
    category: 'Essential',
    purpose: 'CSRF protection token to prevent cross-site request forgery attacks on form submissions.',
    duration: 'Session',
    firstParty: true,
  },
  {
    name: '__stripe_mid',
    category: 'Payment',
    purpose: 'Set by Stripe on the billing/payment page to detect and prevent fraud during payment processing.',
    duration: '1 year',
    firstParty: false,
  },
  {
    name: '__stripe_sid',
    category: 'Payment',
    purpose: 'Stripe session identifier for secure payment transactions.',
    duration: '30 minutes',
    firstParty: false,
  },
];

const CATEGORY_COLORS = {
  Essential: '#00E5FF',
  Functional: '#7C3AED',
  Payment: '#f59e0b',
  Analytics: '#10b981',
};

export default function CookiePolicy() {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <PageLayout
      badge="Legal"
      badgeColor="purple"
      title="Cookie Policy"
      subtitle="What cookies we use, why we use them, and how to control them."
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14">
        <div className="flex gap-14">

          {/* Sticky TOC */}
          <aside className="hidden lg:block w-56 flex-shrink-0">
            <div className="sticky top-20">
              <div className="text-[10px] font-bold text-slate-600 uppercase tracking-widest mb-3">Contents</div>
              <nav className="space-y-0.5">
                {TOC.map(({ id, label }) => (
                  <button
                    key={id}
                    onClick={() => scrollTo(id)}
                    className="w-full text-left px-3 py-2 rounded-lg text-xs text-slate-500 hover:text-[#00E5FF] hover:bg-[#00E5FF]/5 transition-all"
                  >
                    {label}
                  </button>
                ))}
              </nav>
              <div className="mt-6 p-4 glass-card border border-white/[0.06] rounded-xl text-center">
                <Cookie size={16} className="text-amber-400 mx-auto mb-2" />
                <p className="text-[10px] text-slate-500 leading-relaxed">
                  Last updated<br />
                  <span className="text-slate-300 font-medium">May 1, 2026</span>
                </p>
              </div>

              {/* Cookie count summary */}
              <div className="mt-4 p-4 glass-card border border-white/[0.06] rounded-xl">
                <div className="text-[10px] font-bold text-slate-600 uppercase tracking-widest mb-3">Cookie Summary</div>
                {Object.entries(CATEGORY_COLORS).map(([cat, color]) => {
                  const count = COOKIES.filter(c => c.category === cat).length;
                  if (count === 0) return null;
                  return (
                    <div key={cat} className="flex items-center justify-between mb-2">
                      <span className="text-[10px] font-semibold" style={{ color }}>{cat}</span>
                      <span className="text-[10px] text-slate-500">{count} cookie{count !== 1 ? 's' : ''}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </aside>

          {/* Content */}
          <article className="flex-1 min-w-0">

            <div className="mb-10 p-5 glass-card border border-amber-500/15 rounded-xl text-sm text-slate-400 leading-relaxed">
              <strong className="text-white">Effective date:</strong> May 1, 2026. &nbsp;
              This Cookie Policy explains how <strong className="text-white">ZenithCipher, Inc.</strong>{' '}
              uses cookies and similar technologies on our website and platform.
              It should be read alongside our{' '}
              <Link to="/privacy" className="text-[#00E5FF] hover:underline">Privacy Policy</Link>.
            </div>

            <Section id="what-are-cookies" title="1. What Are Cookies?">
              <p>
                Cookies are small text files that a website places on your device (computer, tablet, or phone)
                when you visit. They are widely used to make websites work efficiently and to provide information
                to the site owners.
              </p>
              <p>
                Cookies are not programs or apps — they cannot carry viruses or install software on your device.
                They simply store small pieces of text data that help the website recognize your device on
                future visits.
              </p>
              <p>
                We use cookies that are either <strong className="text-white">first-party</strong> (set
                directly by ZenithCipher) or <strong className="text-white">third-party</strong> (set by
                trusted service providers we use, such as Stripe).
              </p>
            </Section>

            <Section id="how-we-use" title="2. How We Use Cookies">
              <p>
                ZenithCipher uses a minimal, purposeful set of cookies. We categorize them as follows:
              </p>
              <div className="mt-4 space-y-4">
                {[
                  {
                    cat: 'Essential',
                    color: '#00E5FF',
                    desc: 'These cookies are strictly necessary for the platform to function. Without them, core features like login, session management, and CSRF protection would not work. These cannot be disabled.',
                  },
                  {
                    cat: 'Functional',
                    color: '#7C3AED',
                    desc: 'These cookies remember your preferences and settings so you don\'t have to re-configure them every visit. Disabling them may reduce your experience but won\'t break core functionality.',
                  },
                  {
                    cat: 'Payment',
                    color: '#f59e0b',
                    desc: 'Set by Stripe when you visit billing pages. These are required for secure payment processing and fraud prevention. They are subject to Stripe\'s own cookie and privacy policies.',
                  },
                ].map(({ cat, color, desc }) => (
                  <div key={cat} className="flex items-start gap-4 p-4 glass-card border border-white/[0.06] rounded-xl">
                    <span
                      className="text-[10px] font-bold px-2.5 py-1 rounded-full flex-shrink-0 mt-0.5"
                      style={{ background: `${color}15`, color, border: `1px solid ${color}25` }}
                    >
                      {cat}
                    </span>
                    <p className="text-sm text-slate-400 leading-relaxed">{desc}</p>
                  </div>
                ))}
              </div>
            </Section>

            <Section id="cookie-table" title="3. Cookie Reference">
              <p>The following table lists every cookie set by or on behalf of ZenithCipher:</p>
              <div className="mt-4 glass-card border border-white/[0.06] overflow-hidden rounded-xl">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[640px]">
                    <thead>
                      <tr className="text-[11px] text-slate-600 uppercase tracking-wider border-b border-white/[0.05]">
                        <th className="text-left px-5 py-3 font-medium">Name</th>
                        <th className="text-left px-5 py-3 font-medium">Category</th>
                        <th className="text-left px-5 py-3 font-medium">Purpose</th>
                        <th className="text-left px-5 py-3 font-medium">Duration</th>
                        <th className="text-left px-5 py-3 font-medium">1st Party</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/[0.04]">
                      {COOKIES.map(({ name, category, purpose, duration, firstParty }) => (
                        <tr key={name} className="hover:bg-white/[0.02] transition-colors">
                          <td className="px-5 py-4 font-mono text-xs text-slate-300">{name}</td>
                          <td className="px-5 py-4">
                            <span
                              className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                              style={{
                                background: `${CATEGORY_COLORS[category]}15`,
                                color: CATEGORY_COLORS[category],
                              }}
                            >
                              {category}
                            </span>
                          </td>
                          <td className="px-5 py-4 text-xs text-slate-500 leading-relaxed max-w-xs">{purpose}</td>
                          <td className="px-5 py-4 text-xs text-slate-500 whitespace-nowrap">{duration}</td>
                          <td className="px-5 py-4">
                            {firstParty
                              ? <CheckCircle size={14} className="text-green-400" />
                              : <XCircle size={14} className="text-slate-600" />
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </Section>

            <Section id="no-tracking" title="4. What We Don't Do">
              <p>
                We are deliberate about the cookies we <em>don't</em> use:
              </p>
              <div className="mt-3 grid sm:grid-cols-2 gap-3">
                {[
                  { label: 'No advertising cookies',         desc: 'We do not run ad campaigns or use advertising networks that track you across the web.' },
                  { label: 'No cross-site tracking',         desc: 'Our cookies are scoped to zenithcipher.io only. We have no tracking pixels or third-party scripts that follow you elsewhere.' },
                  { label: 'No analytics cookies',           desc: 'We use server-side aggregated analytics only. No Google Analytics, Mixpanel, Segment, or similar client-side tracking.' },
                  { label: 'No fingerprinting',              desc: 'We do not use browser fingerprinting or other tracking techniques that bypass cookie controls.' },
                  { label: 'No social media pixels',         desc: 'No Facebook Pixel, Twitter Pixel, or similar social network tracking on our platform.' },
                  { label: 'No sold data',                   desc: 'Cookie data is never sold or shared with data brokers.' },
                ].map(({ label, desc }) => (
                  <div key={label} className="flex items-start gap-3 p-4 glass-card border border-white/[0.06] rounded-xl">
                    <XCircle size={14} className="text-slate-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="text-xs font-semibold text-slate-300 mb-0.5">{label}</div>
                      <div className="text-xs text-slate-500 leading-relaxed">{desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Section>

            <Section id="third-party" title="5. Third-Party Cookies">
              <p>
                The only third-party cookies set on the ZenithCipher platform are those set by{' '}
                <strong className="text-white">Stripe</strong> on billing and payment pages.
                These cookies are necessary for secure payment processing and Stripe's fraud
                prevention systems.
              </p>
              <p>
                Stripe's cookie and privacy practices are governed by the{' '}
                <a
                  href="https://stripe.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#00E5FF] hover:underline"
                >
                  Stripe Privacy Policy
                </a>.
              </p>
              <p>
                Stripe cookies are only set when you navigate to a billing-related page.
                If you never visit those pages (e.g., you only use the free plan), no Stripe
                cookies will be present on your device.
              </p>
            </Section>

            <Section id="managing" title="6. Managing &amp; Disabling Cookies">
              <p>
                You can control and manage cookies through your browser settings. Here's how to
                access cookie settings in major browsers:
              </p>
              <div className="mt-4 grid sm:grid-cols-2 gap-3">
                {[
                  { browser: 'Google Chrome',    url: 'https://support.google.com/chrome/answer/95647' },
                  { browser: 'Mozilla Firefox',  url: 'https://support.mozilla.org/kb/cookies-information-websites-store-on-your-computer' },
                  { browser: 'Safari',           url: 'https://support.apple.com/guide/safari/manage-cookies-sfri11471' },
                  { browser: 'Microsoft Edge',   url: 'https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge' },
                ].map(({ browser, url }) => (
                  <a
                    key={browser}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 p-3 glass-card border border-white/[0.06] rounded-lg text-xs text-slate-400 hover:text-[#00E5FF] hover:border-[#00E5FF]/20 transition-all"
                  >
                    <Cookie size={12} className="text-amber-400 flex-shrink-0" />
                    {browser} cookie settings
                  </a>
                ))}
              </div>
              <div className="mt-5 p-4 glass-card border border-amber-500/15 bg-amber-500/5 rounded-xl text-sm text-amber-400/80">
                <strong className="text-amber-400">Note:</strong> Disabling essential cookies will
                prevent you from logging in to ZenithCipher. Functional cookies can be disabled
                without losing access, but your preferences will not be saved between sessions.
              </div>
            </Section>

            <Section id="consent" title="7. Your Consent">
              <p>
                By using the ZenithCipher platform, you consent to the use of essential cookies
                as described in this policy. Essential cookies are necessary for the platform to
                function and do not require explicit opt-in under most privacy frameworks.
              </p>
              <p>
                For functional cookies (preference storage), we ask for consent on your first
                visit via our cookie notice banner. You can withdraw consent at any time by
                clearing cookies in your browser or adjusting settings in your account preferences.
              </p>
              <p>
                If you are in the European Economic Area, UK, or another region with specific
                consent requirements, our cookie banner provides granular opt-in controls for
                non-essential cookie categories.
              </p>
            </Section>

            <Section id="changes" title="8. Changes to This Policy">
              <p>
                We may update this Cookie Policy when we add new features that require cookies,
                change our service providers, or in response to regulatory requirements.
                We will update the "Effective date" at the top of this page and, for material
                changes, notify you via email or an in-app notice.
              </p>
            </Section>

            <Section id="contact" title="9. Contact Us">
              <p>
                Questions about our use of cookies or this policy? Contact our Privacy Team:
              </p>
              <div className="mt-4 p-5 glass-card border border-white/[0.06] rounded-xl flex items-start gap-4">
                <div className="w-9 h-9 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center flex-shrink-0">
                  <Mail size={15} className="text-amber-400" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-white mb-0.5">ZenithCipher Privacy Team</div>
                  <a href="mailto:privacy@zenithcipher.io" className="text-[#00E5FF] text-sm hover:underline">
                    privacy@zenithcipher.io
                  </a>
                  <div className="text-xs text-slate-500 mt-1">ZenithCipher, Inc. · San Francisco, CA</div>
                </div>
              </div>
            </Section>

          </article>
        </div>
      </div>
    </PageLayout>
  );
}
